import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { usePillarsPageData } from '../hooks/usePillarsPageData';
import { useParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import { useFilterContext } from '../context/FilterContext';
import { SectionCard, SectionHeader, SectionLoading, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard } from './ui/chartStyles';
import { formatCompact } from './ui/charts';

// Fallback color palette for pillars if FilterContext colors aren't available
const FALLBACK_PILLAR_COLORS = [
  '#BF6E50', // Religion (brown-red)
  '#8CB369', // Sports (green)
  '#D96E48', // Institutional (orange)
  '#D98E48', // History (orange-brown)
  '#B58CB3', // DEI (purple)
  '#ECBF68', // Engineering (yellow)
  '#D9A679', // Music (tan)
  '#56A3A6', // Science (teal)
  '#BFB3A8', // Aeronautics (light gray)
  '#47A363', // Innovation (green)
  '#A6923A', // Sustainability (olive)
  '#5E5E5E', // Law (dark gray)
];

interface PillarEngagementOverTimeProps {
  selectedPillars?: string[];
  selectedBrands?: string[];
  startDate?: string | null;
  endDate?: string | null;
}

const PillarEngagementOverTime: React.FC<PillarEngagementOverTimeProps> = ({
  selectedPillars = [],
  startDate = null,
  endDate = null,
  selectedBrands = [],
}) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Get filter context for platforms and pillar colors
  const { selectedPlatforms, pillarColors, pillarColorsLoading } = useFilterContext();
  
  // State for the active engagement metric ('average' or 'total')
  const [engagementMetric, setEngagementMetric] = useState<'average' | 'total'>('average');
  const cs = useChartStyles();
  
  // Function to get pillar color (from FilterContext or fallback)
  const getPillarColor = (pillar: string, index: number) => {
    if (!pillarColorsLoading && pillarColors[pillar]) {
      return pillarColors[pillar];
    }
    return FALLBACK_PILLAR_COLORS[index % FALLBACK_PILLAR_COLORS.length];
  };
  
  // Handle toggle change
  const handleMetricChange = (event: React.MouseEvent<HTMLElement>, newMetric: 'average' | 'total') => {
    if (newMetric !== null) {
      setEngagementMetric(newMetric);
    }
  };
  
  // Fetch data using our hook
  const { data, loading, error } = usePillarsPageData({
    orgId,
    pillar: selectedPillars.length === 1 ? selectedPillars[0] : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined
  });
  
  // Process data for the chart
  const chartData = useMemo(() => {
    if (!data) return [];

    // Determine all relevant months from the data
    const allMonths = new Set<string>();
    if (data.monthly_pillar_metrics) {
      Object.values(data.monthly_pillar_metrics).forEach(pillarData => {
        pillarData.forEach(item => allMonths.add(item.month_year));
      });
    }
    // Also consider months from brand-specific data if filtering by brand
    if (data.brand_pillar_metrics) {
      Object.values(data.brand_pillar_metrics).forEach(brandData => {
        Object.values(brandData).forEach(pillarData => {
          pillarData.monthly_metrics?.forEach(item => allMonths.add(item.month_year));
        });
      });
    }
    const sortedMonths = Array.from(allMonths).sort();

    // Use selectedBrands prop directly
    const brandsToUse = selectedBrands;
    const shouldFilterByBrands = brandsToUse && brandsToUse.length > 0;

    return sortedMonths.map(month => {
      const monthData: { [key: string]: any } = { month }; // Use 'month' as the key
      const relevantPillars = selectedPillars.length > 0
        ? data.pillars.filter(p => selectedPillars.includes(p))
        : data.pillars;

      relevantPillars.forEach(pillar => {
        let totalEngagementSum = 0;
        let totalPostCount = 0;

        if (shouldFilterByBrands) {
          // Aggregate from selected brands' monthly data
          brandsToUse.forEach(brand => {
            const brandPillarData = data.brand_pillar_metrics[brand]?.[pillar]; // Get overall brand/pillar data
            const brandMonthlyData = brandPillarData?.monthly_metrics?.find(m => m.month_year === month);

            if (brandMonthlyData && brandPillarData) {
              const platformsToFilter = selectedPlatforms && selectedPlatforms.length > 0;
              const hasPlatformData = brandPillarData.platforms && Object.keys(brandPillarData.platforms).length > 0;
              let includeBrandMonthlyData = true; // Default to include

              if (platformsToFilter && hasPlatformData) {
                // Check if any selected platform contributed *overall* for this brand/pillar
                let platformContributedOverall = false;
                Object.keys(brandPillarData.platforms || {}).forEach(platform => {
                  if (selectedPlatforms.includes(platform.toLowerCase())) {
                    platformContributedOverall = true;
                  }
                });
                // Only include this brand's monthly data if a selected platform contributed overall
                includeBrandMonthlyData = platformContributedOverall;
              }

              if (includeBrandMonthlyData) {
                if (engagementMetric === 'average') {
                  totalEngagementSum += brandMonthlyData.avg_engagement * brandMonthlyData.post_count;
                  totalPostCount += brandMonthlyData.post_count;
                } else {
                  totalEngagementSum += brandMonthlyData.sum_engagement;
                }
              }
            }
          });

          // Calculate final value for this pillar/month based on brand aggregation
          if (engagementMetric === 'average' && totalPostCount > 0) {
            monthData[pillar] = totalEngagementSum / totalPostCount;
          } else if (engagementMetric === 'total') {
            monthData[pillar] = totalEngagementSum;
          } else {
            monthData[pillar] = 0;
          }

        } else {
          // Original logic: Use overall monthly metrics and apply platform filtering
          const pillarMonthData = data.monthly_pillar_metrics[pillar]?.find(item => item.month_year === month);
          if (pillarMonthData) {
            let pillarValue = 0;
            let postCountForAvg = 0;

            // Apply platform filter if needed
            if (selectedPlatforms && selectedPlatforms.length > 0 && pillarMonthData.platforms) {
              Object.entries(pillarMonthData.platforms || {}).forEach(([platform, platformData]: [string, any]) => {
                if (selectedPlatforms.includes(platform.toLowerCase())) {
                  if (engagementMetric === 'average') {
                    pillarValue += platformData.avg_engagement * platformData.post_count;
                    postCountForAvg += platformData.post_count;
                  } else {
                    pillarValue += platformData.sum_engagement;
                  }
                }
              });

              // Finalize average if needed
              if (engagementMetric === 'average' && postCountForAvg > 0) {
                monthData[pillar] = pillarValue / postCountForAvg;
              } else if (engagementMetric === 'total') {
                monthData[pillar] = pillarValue;
              } else {
                 monthData[pillar] = 0; // Handle case where post count is 0 for selected platforms
              }
            } else {
              // No platform filter, use the overall monthly value
              monthData[pillar] = engagementMetric === 'average'
                ? pillarMonthData.avg_engagement
                : pillarMonthData.sum_engagement;
            }
          } else {
            monthData[pillar] = 0;
          }
        }
      });

      return monthData;
    });
  }, [data, engagementMetric, selectedPillars, selectedBrands, selectedPlatforms]); // Use selectedBrands prop directly
  
  // Format X-axis tick labels
  const formatXAxisTick = (value: string) => {
    // Convert YYYY-MM to MMM-YYYY format
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  };
  
  // Format Y-axis tick labels with "k" notation for large numbers
  const formatYAxisTick = (value: number) => formatCompact(value);
  
  // Calculate bar size based on number of months
  const getBarSize = () => {
    if (!chartData || chartData.length === 0) return 20;
    
    if (chartData.length <= 3) {
      return 80; // Wider bars for few months
    } else if (chartData.length <= 6) {
      return 60;
    } else if (chartData.length <= 12) {
      return 30;
    } else {
      return 20; // Default size for many months
    }
  };

  // Number formatter
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', { 
      maximumFractionDigits: 0,  // Round to whole numbers
      minimumFractionDigits: 0   // Don't show decimals
    }).format(Math.round(value));  // Round before formatting
  };
  
  // Custom tooltip component for better formatting
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Sort payload by value in descending order
      const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
      return (
        <ChartTooltipCard
          title={formatXAxisTick(label)}
          rows={sortedPayload.map((entry: any, index: number) => ({
            key: `tooltip-${index}`,
            color: entry.color,
            label: entry.name,
            value: formatNumber(entry.value),
          }))}
        />
      );
    }

    return null;
  };

  const header = (actions?: React.ReactNode) => (
    <SectionHeader
      title="Pillar Engagement Over Time"
      info="Track engagement patterns across content pillars over time. Toggle between average (engagement per post) and total (cumulative engagement) to compare pillar performance intensity vs. volume."
      actions={actions}
    />
  );

  if (loading && !data) {
    return (
      <SectionCard>
        {header()}
        <SectionLoading height={340} />
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard>
        {header()}
        <Alert severity="error">{error}</Alert>
      </SectionCard>
    );
  }

  if (!data || Object.keys(data.monthly_pillar_metrics).length === 0) {
    return (
      <SectionCard>
        {header()}
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          No pillar data for these filters.
        </Typography>
      </SectionCard>
    );
  }

  // Filter pillars if selectedPillars is provided
  const pillarsToDisplay = selectedPillars.length > 0
    ? data.pillars.filter(p => selectedPillars.includes(p))
    : data.pillars;
  
  return (
    <SectionCard>
      {header(
        <ToggleButtonGroup
          value={engagementMetric}
          exclusive
          onChange={handleMetricChange}
          aria-label="engagement metric"
          size="small"
        >
          <ToggleButton value="average">
            Average Engagement
          </ToggleButton>
          <ToggleButton value="total">
            Total Engagement
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mb: 2.5 }}>
        {pillarsToDisplay.map((pillar, index) => (
          <LegendItem key={pillar} color={getPillarColor(pillar, index)} label={pillar} />
        ))}
      </Box>

      <Box sx={{ width: '100%', height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            barSize={getBarSize()}
            barGap={2}
          >
            <CartesianGrid {...cs.grid} />
            <XAxis
              {...cs.xAxis}
              dataKey="month"
              tickFormatter={formatXAxisTick}
            />
            <YAxis
              {...cs.yAxis}
              width={64}
              label={cs.axisLabel(engagementMetric === 'average' ? 'Mean engagement score' : 'Total engagement score')}
              tickFormatter={formatYAxisTick}
            />
            <RechartsTooltip
              content={CustomTooltip}
              cursor={cs.cursor}
            />
            {pillarsToDisplay.map((pillar, index) => (
              <Bar
                key={pillar}
                dataKey={pillar}
                fill={getPillarColor(pillar, index)}
                name={pillar}
                radius={engagementMetric === 'total' ? 0 : [3, 3, 0, 0]}
                stackId={engagementMetric === 'total' ? "stack" : undefined}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
};

export default PillarEngagementOverTime; 